import ModalWrapper from "./modalWrapper";
import {Box, IconButton, Stack, Typography, useTheme} from "@mui/material";
import {ThatsMeLoadingButton} from "../buttons/thatsMeLoadingButton";
import React, {useContext, useMemo, useState} from "react";
import {Download} from "@carbon/icons-react";
import {TwitterShareButton, WhatsappShareButton} from "react-share";
import ShareableLink from "../buttons/shareableLink";
import TwitterLogo from "../../common/icons/social/twitterLogo";
import WhatsAppLogo from "../../common/icons/social/whatsAppLogo";
import ShareViaIcon from "../../common/icons/social/shareVia";
import {ThatsMeBabyContext} from "../../common/contexts/thatsMeBabyContext";
import {EnvironmentConfig} from "../../common/configs/environment.config";
import {ErrorHelper} from "../../common/utils/error.helper";
import {AxiosError} from "axios";
import {toast} from "react-toastify";
import {userProfileGetPersonaImage} from "../../api/userProfileService";
import {AbortError} from "fork-ts-checker-webpack-plugin/lib/utils/async/abort-error";

interface ShareModalProps {
    openModal: boolean,
    onClose: () => void
}

const ShareModal: React.FC<ShareModalProps> = ({openModal , onClose}) => {

    const {profile} = useContext(ThatsMeBabyContext);
    const theme = useTheme();
    
    const [showShareButton] = useState(Boolean(navigator.share));
    const [loading, setLoading] = useState<boolean>(false);

    const userCustomURL = useMemo(() => `${EnvironmentConfig.BaseUrl}/${profile?.customUrlPathName}`, [profile]);

    const userPersonaName = useMemo(() => {
        var userPersonaResult = profile?.userCategoryResults.find((ucr) => ucr?.category?.title == "Persona");
        return userPersonaResult?.answer?.label;
    }, [profile]);
    

    const openShareViaScreen = async () => {
        if (!navigator.share && !navigator.canShare()) {
            return;
        }

        const message: ShareData = {
            title: `${EnvironmentConfig.SharedLinkTitle} ${userPersonaName}.`,
            text: `${EnvironmentConfig.SharedLinkTitle} ${userPersonaName}.`,
            url: `${EnvironmentConfig.BaseUrl}/${profile?.customUrlPathName}`
        };
        
        if(!navigator.canShare(message)) {
            return;
        }

        try{
            await navigator.share(message);
        }
        catch(e) {
            const error = e as AbortError;
            toast.error(error.message);
        }
    }

    const handleDownloadImage = async () => {
        setLoading(true);

        try {
            const response = await userProfileGetPersonaImage();

            // Create blob link to download
            const url = window.URL.createObjectURL(
                new Blob([response.blob]),
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                response.filename,
            );

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            ErrorHelper.showErrors(e as AxiosError);
        }
        setLoading(false);
    }

    return (
        <ModalWrapper openModal={openModal} onClose={onClose} modalFixedHeight={"345px"} modalFixedWidth={'400px'} >
            <Stack flex={1} gap={3} pt={1} px={0} alignItems={"center"} >
                <Typography variant={"subtitle1"} color={theme.palette.thatsMe.textTitle} fontWeight={700} >Share this page</Typography>
                <Box width={"80%"} display={"flex"} justifyContent={"space-between"} >
                    <TwitterShareButton url={userCustomURL} title={`${EnvironmentConfig.SharedLinkTitle} ${userPersonaName}.`} >
                        <TwitterLogo />
                        <Typography pt={0.5} variant={"shareButtonText"} justifyContent={"center"} >X</Typography>
                    </TwitterShareButton>
                    <WhatsappShareButton url={userCustomURL} title={`${EnvironmentConfig.SharedLinkTitle} ${userPersonaName}.`} >
                        <WhatsAppLogo />
                        <Typography pt={0.5} variant={"shareButtonText"} justifyContent={"center"} >Whatsapp</Typography>
                    </WhatsappShareButton>
                    {showShareButton &&
                        <IconButton onClick={openShareViaScreen} className={"share-via-button"} disableRipple 
                                    sx={{ '&.Mui-focusVisible': {
                                        borderRadius: 0,
                                        outline: '1px solid #0f0f0f',
                                        outlineOffset: '1px',
                                        outlineStyle: 'auto'
                                    }}}>
                            <ShareViaIcon />
                            <Typography pt={1} variant={"shareButtonText"} justifyContent={"center"}>Share via</Typography>
                        </IconButton>
                    }
                </Box>
                <ShareableLink/>
                <ThatsMeLoadingButton fullWidth variant={"outlined"} onClick={handleDownloadImage} loading={loading}>
                    <Download/>&nbsp;Download Image
                </ThatsMeLoadingButton>
            </Stack>
        </ModalWrapper>
    );
}

export default ShareModal